import {
  type QuarterlyForecastTechnicalCementPowerConsumptionsHierarchy,
  type TechnicalCementPowerConsumptionResponse,
  type MtmYtmRequestPayload,
  formatDateUtcToTimezoneDateOnly,
  PERFORMANCE_KPI_REQUEST_STALE_TIME
} from '@domain/kpis'
import {ApiClient} from '@hconnect/apiclient'
import {Iso8601} from '@hconnect/uikit'
import {useQuery, UseQueryOptions} from '@tanstack/react-query'
import {AxiosError} from 'axios'

export const getTechnicalCementPowerConsumptionMtmYtmUrlSuffix = (showYTM?: boolean) =>
  showYTM ? 'technical-cement-power-consumption-ytm' : 'technical-cement-power-consumption-mtm'

export const getTechnicalCementPowerConsumptionMtmYtmQueryKeys = (
  requestPayload: MtmYtmRequestPayload
) => [
  getTechnicalCementPowerConsumptionMtmYtmUrlSuffix(requestPayload.showYTM),
  formatDateUtcToTimezoneDateOnly(requestPayload.timestamp, requestPayload.timezone),
  requestPayload.locationId
]

export const getTechnicalCementPowerConsumption = async <T>(
  {timestamp, timezone, locationId, showYTM}: MtmYtmRequestPayload,
  signal?: AbortSignal
) => {
  const referenceDate = formatDateUtcToTimezoneDateOnly(timestamp, timezone)
  const url = `/kpi/dashboard/${getTechnicalCementPowerConsumptionMtmYtmUrlSuffix(showYTM)}`

  const response = await ApiClient.axiosInstance.get<T>(url, {
    params: {locationId, date: referenceDate},
    signal
  })

  return response.data
}

export const useTechnicalCementPowerConsumption = <T>(
  payload: MtmYtmRequestPayload,
  options?: Omit<
    UseQueryOptions<TechnicalCementPowerConsumptionResponse, AxiosError, T>,
    'queryKey' | 'queryFn'
  >
) => {
  const QueryKeys = getTechnicalCementPowerConsumptionMtmYtmQueryKeys(payload)

  return useQuery<TechnicalCementPowerConsumptionResponse, AxiosError, T>({
    queryKey: QueryKeys,
    queryFn: async ({signal}) => {
      return getTechnicalCementPowerConsumption(payload, signal)
    },
    staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME,
    ...options
  })
}

export const useTechnicalCementPowerConsumptionsQuarterly = (
  timestamp: Iso8601,
  timezone: string,
  locationId?: string,
  options?: Omit<
    UseQueryOptions<QuarterlyForecastTechnicalCementPowerConsumptionsHierarchy | null, AxiosError>,
    'queryKey' | 'queryFn'
  >
) => {
  const referenceDate = formatDateUtcToTimezoneDateOnly(timestamp, timezone)

  return useQuery<QuarterlyForecastTechnicalCementPowerConsumptionsHierarchy | null, AxiosError>({
    queryKey: ['technical-cement-power-consumption-quarterly', referenceDate, locationId],
    queryFn: async ({signal}) => {
      if (!locationId) return Promise.resolve(null)

      const response =
        await ApiClient.axiosInstance.get<QuarterlyForecastTechnicalCementPowerConsumptionsHierarchy>(
          '/kpi/dashboard/technical-cement-power-consumption-qf',
          {
            params: {locationId, timestamp: referenceDate},
            signal
          }
        )
      return response.data
    },
    staleTime: PERFORMANCE_KPI_REQUEST_STALE_TIME,
    ...options
  })
}
